/**
 * Hook for managing AI settings stored in localStorage
 */

import { useState, useEffect, useCallback } from 'react';
import { AiSettings, AiPromptTemplates, DEFAULT_AI_SETTINGS } from '@/types/ai';
import { DEFAULT_PROMPT_TEMPLATES } from '@/services/aiPrompts';

const STORAGE_KEY = 'Meshpit-ai-settings';

/**
 * Get default settings with prompt templates
 */
function getDefaultSettings(): AiSettings {
  return {
    ...DEFAULT_AI_SETTINGS,
    promptTemplates: { ...DEFAULT_PROMPT_TEMPLATES },
  };
}

/**
 * Load settings from localStorage
 */
function loadSettings(): AiSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return getDefaultSettings();
    }

    const parsed = JSON.parse(stored) as Partial<AiSettings>;

    // Merge with defaults to handle missing fields
    return {
      apiKey: parsed.apiKey ?? DEFAULT_AI_SETTINGS.apiKey,
      defaultModel: parsed.defaultModel ?? DEFAULT_AI_SETTINGS.defaultModel,
      promptTemplates: {
        analyze:
          parsed.promptTemplates?.analyze ?? DEFAULT_PROMPT_TEMPLATES.analyze,
        plan: parsed.promptTemplates?.plan ?? DEFAULT_PROMPT_TEMPLATES.plan,
        implement:
          parsed.promptTemplates?.implement ??
          DEFAULT_PROMPT_TEMPLATES.implement,
        fix: parsed.promptTemplates?.fix ?? DEFAULT_PROMPT_TEMPLATES.fix,
      },
    };
  } catch (error) {
    console.error('Failed to load AI settings:', error);
    return getDefaultSettings();
  }
}

/**
 * Save settings to localStorage
 */
function saveSettings(settings: AiSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save AI settings:', error);
  }
}

export interface UseAiSettingsReturn {
  // Settings
  settings: AiSettings;
  isConfigured: boolean;

  // Setters
  setApiKey: (key: string) => void;
  setDefaultModel: (model: string) => void;
  setPromptTemplate: (key: keyof AiPromptTemplates, value: string) => void;

  // Actions
  resetPromptTemplates: () => void;
  resetAllSettings: () => void;
  validateApiKey: (key: string) => boolean;
}

/**
 * Hook for managing AI settings
 */
export function useAiSettings(): UseAiSettingsReturn {
  const [settings, setSettings] = useState<AiSettings>(getDefaultSettings);

  // Load settings from localStorage on mount
  useEffect(() => {
    setSettings(loadSettings());
  }, []);

  // Save settings whenever they change
  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  const isConfigured = Boolean(settings.apiKey);

  const setApiKey = useCallback((key: string) => {
    setSettings((prev) => ({ ...prev, apiKey: key }));
  }, []);

  const setDefaultModel = useCallback((model: string) => {
    setSettings((prev) => ({ ...prev, defaultModel: model }));
  }, []);

  const setPromptTemplate = useCallback(
    (key: keyof AiPromptTemplates, value: string) => {
      setSettings((prev) => ({
        ...prev,
        promptTemplates: {
          ...prev.promptTemplates,
          [key]: value,
        },
      }));
    },
    [],
  );

  const resetPromptTemplates = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      promptTemplates: { ...DEFAULT_PROMPT_TEMPLATES },
    }));
  }, []);

  const resetAllSettings = useCallback(() => {
    setSettings(getDefaultSettings());
  }, []);

  const validateApiKey = useCallback((key: string): boolean => {
    // OpenRouter API keys start with "sk-or-" and are typically 64+ chars
    if (!key) return false;
    if (key.length < 20) return false;
    // Basic format check
    return key.startsWith('sk-or-') || key.startsWith('sk-');
  }, []);

  return {
    settings,
    isConfigured,
    setApiKey,
    setDefaultModel,
    setPromptTemplate,
    resetPromptTemplates,
    resetAllSettings,
    validateApiKey,
  };
}
